import { useId } from "react";
import { useQuery } from "@connectrpc/connect-query";
import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { nepalToday } from "@yatayat/core/date";
import { formatNumber } from "@yatayat/core/money";
import { ConsoleService, VehicleDocument, type DocumentExpiry } from "@yatayat/gen/yatayat/console/v1/console_pb";

import { FilterSelect, OperatorSelect } from "~/components/console/ComplianceKit";
import { ExpiryTable, documentLabels, documentNames } from "~/components/console/ExpiryTable";
import { useStaffSessionExpiry } from "~/components/OperatorTrip";
import { QueryError } from "~/components/QueryState";
import { csvFilename, downloadCsv, type CsvCell } from "~/lib/csv";
import { useI18n } from "~/lib/i18n";
import { retryTransient } from "~/lib/rpc/errors";
import { searchString } from "~/lib/search";
import { ui } from "~/lib/ui";

type ExpirySearch = { within?: number; expired?: boolean; document?: string; operator?: string };

const windows = [7, 30, 60, 90] as const;
const defaultWindow = 30;
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Filters live in the URL so a filtered list can be shared or reopened.
export const Route = createFileRoute("/console/expiry")({
  validateSearch: (search: Record<string, unknown>): ExpirySearch => {
    const within = Number(search.within);
    return {
      within: windows.some((w) => w === within) ? within : undefined,
      // Lapsed papers are the most urgent, so they show unless switched off.
      expired: search.expired === false || search.expired === "false" ? false : undefined,
      document: searchString(search.document),
      operator: searchString(search.operator),
    };
  },
  component: ConsoleExpiry,
});

function ConsoleExpiry() {
  const { t, lang } = useI18n();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const expiredId = useId();

  const within = search.within ?? defaultWindow;
  const includeExpired = search.expired !== false;
  const documentName = search.document && Object.hasOwn(documentNames, search.document) ? search.document : "";
  const operatorId = search.operator && uuid.test(search.operator) ? search.operator : "";

  const expiring = useQuery(
    ConsoleService.method.listExpiringDocuments,
    {
      withinDays: within,
      includeExpired,
      document: documentName ? documentNames[documentName] : VehicleDocument.UNSPECIFIED,
      operatorId,
    },
    { retry: retryTransient, placeholderData: keepPreviousData },
  );
  const unauthenticated = useStaffSessionExpiry(expiring.error);
  const documents = expiring.data?.documents ?? [];

  function exportCsv(rows: DocumentExpiry[]) {
    // Dates stay ISO and numbers ASCII, so spreadsheets sort and filter them.
    const header: CsvCell[] = [
      t("plateNo"),
      t("aboutSticker"),
      t("operator"),
      t("consoleExpiryDocument"),
      t("consoleExpiryReference"),
      t("consoleExpiryExpiresOn"),
      t("consoleExpiryDaysLeftHeading"),
    ];
    const body = rows.map((d): CsvCell[] => [
      d.plateNo,
      d.stickerCode,
      d.operatorName?.[lang] ?? "",
      t(documentLabels[d.document]),
      d.reference,
      d.expiresOn,
      d.daysLeft,
    ]);
    downloadCsv(csvFilename("expiring-documents", nepalToday()), [header, ...body]);
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className={ui.h1}>{t("consoleExpiryTitle")}</h1>
        <p className={ui.muted}>{t("consoleExpiryIntro")}</p>
      </div>

      <fieldset className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[repeat(3,minmax(0,1fr))_auto] lg:items-end">
        <legend className="sr-only">{t("consoleFilterLegend")}</legend>
        <FilterSelect
          label={t("consoleExpiryWithin")}
          value={String(within)}
          onChange={(v) => {
            const days = Number(v);
            void navigate({ search: (prev) => ({ ...prev, within: days === defaultWindow ? undefined : days }), replace: true, resetScroll: false });
          }}
          options={windows.map((w) => ({ value: String(w), label: t("consoleExpiryWithinDays", { n: formatNumber(w, lang) }) }))}
        />
        <FilterSelect
          label={t("consoleExpiryDocument")}
          value={documentName}
          onChange={(v) => void navigate({ search: (prev) => ({ ...prev, document: v || undefined }), replace: true, resetScroll: false })}
          options={[
            { value: "", label: t("consoleExpiryAllDocuments") },
            ...Object.entries(documentNames).map(([name, d]) => ({ value: name, label: t(documentLabels[d]) })),
          ]}
        />
        <OperatorSelect
          value={operatorId}
          onChange={(v) => void navigate({ search: (prev) => ({ ...prev, operator: v || undefined }), replace: true, resetScroll: false })}
        />
        <div className="flex items-center gap-2 py-2 text-sm">
          <input
            id={expiredId}
            type="checkbox"
            checked={includeExpired}
            onChange={(e) => void navigate({ search: (prev) => ({ ...prev, expired: e.target.checked ? undefined : false }), replace: true, resetScroll: false })}
            className="h-4 w-4"
          />
          <label htmlFor={expiredId} className="font-medium">
            {t("consoleExpiryIncludeExpired")}
          </label>
        </div>
      </fieldset>

      <section aria-labelledby={`${expiredId}-results`} aria-busy={expiring.isFetching} className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id={`${expiredId}-results`} className="sr-only">
            {t("consoleExpiryResults")}
          </h2>
          <p aria-live="polite" className={ui.muted}>
            {expiring.data && t("consoleExpiryShown", { n: formatNumber(documents.length, lang) })}
          </p>
          <button
            type="button"
            onClick={() => exportCsv(documents)}
            disabled={documents.length === 0 || expiring.isPlaceholderData}
            className={ui.primary}
          >
            {t("consoleExpiryExportCsv")}
          </button>
        </div>

        {expiring.isPending && <p className={ui.muted}>{t("loading")}</p>}
        {expiring.isError && !expiring.data && !unauthenticated && <QueryError onRetry={() => void expiring.refetch()} busy={expiring.isFetching} />}
        {expiring.data && documents.length === 0 && <p className={ui.empty}>{t("consoleExpiryNone")}</p>}
        {documents.length > 0 && (
          <div className={expiring.isPlaceholderData ? "opacity-60" : undefined}>
            <ExpiryTable documents={documents} />
          </div>
        )}
      </section>
    </div>
  );
}
